import { useMemo, useRef } from 'react'

/**
 * Live alert-tile visibility TTL (.claude/work/SPEC.md). A live alert tile stops being
 * shown 5 minutes after the alert's OWN `ts` (epoch ms when it fired) -- not 5 minutes
 * from when the client first rendered it. Pure UI-layer filter; no backend change.
 *
 * Applies ONLY to live, currently-on-screen alert-tile surfaces (per-vehicle exception
 * chips, live alert feeds/tabs). Historical/playback/report surfaces must keep showing
 * alerts regardless of age and must not call this.
 */
export const ALERT_VISIBILITY_TTL_MS = 5 * 60_000

/** Inclusive at the boundary: an alert exactly `ttlMs` old is still visible. An
 *  arbitrary-but-fine choice at the edge -- `now` only ever increases, so the boundary
 *  is crossed once and stays crossed, not flickered. */
export function isAlertVisible(ts: number, now: number, ttlMs = ALERT_VISIBILITY_TTL_MS): boolean {
  return now - ts <= ttlMs
}

/**
 * Alert kinds exempted from the ts TTL above (user decision, 2026-10-28): these
 * represent a CONDITION that can still be true well past 5 minutes, not a
 * fired-once/retrospective event. Every one fires at most once per episode and only
 * refires once the underlying condition has ended and recurred -- confirmed by the
 * reset-on-resume debounce guard in each site's own rule module:
 *   - MBMT / Ultratech / Wonder Cement: `EXCESSIVE_IDLING`/`UNSCHEDULED_STOP`
 *     (`quixapp/rules/bus_driving.py`'s `idle_alerted`, `quixapp/rules/geofence.py`'s
 *     `s.idle_alerted` -- both reset only when the vehicle moves again).
 *   - STAR: same two conditions, different kind strings -- `IDLING`/`UNSCHEDULED_STOP`
 *     (`serving/star_rules.py::classify_stop`, "One alert per episode; the episode
 *     resets the moment speed rises above stop_speed_kph").
 *
 * A caller must NOT pass these kinds through `isAlertVisible` -- there is no
 * "resolved" signal on the alert row itself to age by (MBMT's `site_ours_alert` has no
 * such column at all), so the only correct visibility check is the vehicle's CURRENT
 * telemetry (already polled/pushed every cycle): still stopped -> still shown; moving
 * again -> hidden, independent of how old the alert's own `ts` is.
 *
 * Deliberately NOT in this set, despite also firing once per episode --
 * `TAT_BREACH`/`HUB_OVERSTAY`: confirmed backend-side as retrospective, not ongoing
 * (`serving/app.py`'s `ACTIONABLE_ALERT_KINDS` comment: "a TAT breach already happened
 * by the time it fires, and nobody 'resolves' one" -- same category as an overspeed
 * ping, which is why these still get the ordinary ts TTL, not this exemption).
 */
export const SUSTAINED_CONDITION_KINDS: ReadonlySet<string> = new Set([
  'EXCESSIVE_IDLING',
  'IDLING',
  'UNSCHEDULED_STOP',
])

export function isSustainedConditionKind(kind: string): boolean {
  return SUSTAINED_CONDITION_KINDS.has(kind)
}

/**
 * Hard backstop for the sustained-condition exemption below: a sustained alert may
 * never be considered open more than this long after its OWN `ts`, no matter what the
 * "still open" proxy says or how long the resurrection latch has (or hasn't) had a
 * chance to fire -- a ceiling for when the proxy is wrong, not a mechanism expected to
 * bind in the ordinary case.
 *
 * Adversarial review, round 3: this used to be "same IST calendar day", which was
 * wrong on both ends -- (a) it bounded a FRESH alert to as little as a few minutes
 * if it happened to fire just before midnight IST (the exact failure this exemption
 * exists to prevent), and (b) it let a STALE one survive up to ~24h depending on
 * wall-clock time, which does nothing to bound the real exposure: the resurrection
 * latch (`useSustainedAlertDeadSet`) is per-mount and every site unmounts on
 * navigation, so after a remount the backstop -- not the latch -- is what's actually
 * limiting how long a long-dead alert can resurface. A fixed elapsed-time ceiling
 * fixes both: it does not care what wall-clock time `ts` happened to land on, and it
 * bounds the remount-resurrection exposure to this one fixed window instead of up to
 * a day. 90 minutes is well past any of these kinds' own firing thresholds (MBMT's
 * `MBMT_EXCESSIVE_IDLING_SEC`/`MBMT_UNSCHEDULED_STOP_SEC`, the truck/STAR equivalents,
 * all single-digit minutes) -- long enough that a genuinely still-open stop is rarely
 * cut off early, short enough to keep the worst case bounded.
 */
export const SUSTAINED_ALERT_MAX_AGE_MS = 90 * 60_000

export function isWithinSustainedAlertMaxAge(ts: number, now: number): boolean {
  return now - ts <= SUSTAINED_ALERT_MAX_AGE_MS
}

/**
 * Dedicated liveness bound for the sustained-condition exemption's `isLive` gate --
 * deliberately NOT `console/src/lib/format.ts`'s `STALE_AFTER_MS` (45 minutes), which
 * is calibrated for map-pin desaturation, a different, much more forgiving concern.
 * A truck silent for 44 minutes is not "confirmed still idling" on a feature whose own
 * TTL is 5 minutes (adversarial review MED, round 3) -- reuses `ALERT_VISIBILITY_TTL_MS`
 * itself rather than inventing a third unrelated constant.
 */
export const SUSTAINED_ALERT_LIVENESS_MS = ALERT_VISIBILITY_TTL_MS

/**
 * A sustained-condition alert's visibility input, assembled by the CALLER from
 * whatever live telemetry it already has for that alert's vehicle -- this module has
 * no knowledge of any site's Vehicle shape.
 */
export interface SustainedAlertCheck {
  alertId: string
  /** The alert's own firing ts, epoch ms -- only consulted for the max-age backstop. */
  ts: number
  now: number
  /** The vehicle's telemetry is current enough to trust right now (not stale/offline/
   *  never-reported) -- compute this from a DIRECT freshness check (a timestamp
   *  comparison against a short, dedicated bound), never by deriving it from a
   *  composite status helper that short-circuits on an unrelated condition first.
   *  Adversarial review, round 4: STAR's original wiring computed `isLive` from
   *  `starStatus()`, whose `isDown` branch returns `'alert'` BEFORE ever checking
   *  `polled_ts` -- so an open-breakdown truck with HOURS-stale telemetry still read
   *  as live, got excluded (down == excluded state), and got latched dead forever by
   *  a read that was never trustworthy in the first place. FALSE must hide the tile
   *  WITHOUT killing it permanently -- a telemetry gap is not evidence the vehicle
   *  moved (round 3's HIGH-1: MBMT's `moving: false` is also what a never-reported
   *  ghost vehicle synthesizes, and MBMT's actionables query carries no time window of
   *  its own at all). This function enforces that "not live -> never latch" invariant
   *  itself now (isLive is checked before isMoving/isExcludedState, below) rather than
   *  trusting every call site to never pass a wrongly-true isMoving/isExcludedState
   *  alongside isLive=false -- defense in depth for exactly the STAR bug above. */
  isLive: boolean
  /** The vehicle is confirmed moving right now -- the episode is definitely over.
   *  TRUE permanently kills this alertId (adversarial review HIGH-3: resurrection --
   *  without a latch, the SAME alert reappears the next time this vehicle stops for
   *  ANY reason, e.g. a red light 20 minutes later, which is wrong: the producer's own
   *  debounce already treated that resume as the episode ending). */
  isMoving: boolean
  /** The vehicle is confirmed in a state the backend's OWN rule excludes from this
   *  alert kind when that signal is available client-side -- charging, at a hub, etc
   *  (adversarial review HIGH-2: `moving === false` alone is a much weaker predicate
   *  than the producer's actual firing condition, e.g. Ultratech/WC's EXCESSIVE_IDLING
   *  requires `power_kw >= threshold AND NOT charging`, STAR's IDLING requires
   *  `ignition on AND NOT charging`). Treated the same as isMoving: a confirmed
   *  "this episode's basis no longer holds" signal, so it also kills the alertId
   *  permanently -- NOT the same as isLive=false, which is "we don't know". Defaults
   *  false for a site with no such signal available (a documented gap, not an assumed
   *  "definitely still open"). */
  isExcludedState?: boolean
}

export interface SustainedAlertDeadSet {
  isDead: (alertId: string) => boolean
  markDead: (alertId: string) => void
}

/** Per-mount memory of which sustained alertIds have been confirmed over (moved, or
 *  moved into an excluded state) -- resets on reload/remount, bounded by
 *  `SUSTAINED_ALERT_MAX_AGE_MS` regardless (see that constant's own comment: the
 *  latch alone was never the full story, since every site's shell unmounts on
 *  navigation). A `Set` on a ref, not reactive state -- this is bookkeeping, not
 *  something a re-render should ever need to be caused BY.
 *
 *  Adversarial review MED, round 3: the returned object itself must be a STABLE
 *  reference across renders, not a fresh literal every time -- `isDead`/`markDead`
 *  are passed straight into `useMemo` dependency arrays at every call site
 *  (CommandCentre.tsx/WonderCementShell.tsx/StarShell.tsx), and a new object every
 *  render would invalidate those memos every render, defeating the anti-flicker
 *  cadence those memos exist for. `useMemo(..., [])` with a `ref` captured in the
 *  closure (not in the deps) is correct here: `ref` itself is already stable across
 *  renders, so the empty deps array does not go stale. */
export function useSustainedAlertDeadSet(): SustainedAlertDeadSet {
  const ref = useRef<Set<string>>(new Set())
  return useMemo(() => ({
    isDead: (alertId: string) => ref.current.has(alertId),
    markDead: (alertId: string) => { ref.current.add(alertId) },
  }), [])
}

/**
 * Whether a sustained-condition alert is still open. Composes all adversarial-review
 * fixes (staleness gate, excluded-state gate, resurrection latch, max-age backstop)
 * in ONE place, in an order chosen so no call site has to get them right by hand:
 *   1. Already confirmed dead (latched)?                     -> hidden, stays dead.
 *   2. Past its own max-age backstop?                         -> hidden (SUSTAINED_ALERT_MAX_AGE_MS).
 *   3. Telemetry not currently live/trustworthy?               -> hidden, NOT latched.
 *   4. Confirmed moving, or confirmed in an excluded state?    -> hidden, LATCH dead.
 *   5. Otherwise: live, not moving, not excluded, within age   -> still shown.
 *
 * Step 3 runs BEFORE step 4 deliberately (round 4 fix): a caller that (by mistake,
 * the way STAR's original wiring did) passes isMoving/isExcludedState=true alongside
 * isLive=false can no longer latch an alertId dead on a read that was never
 * trustworthy -- isLive=false short-circuits first, every time, regardless of what
 * the other two fields say.
 *
 * `markDead` runs as a side effect of this call, typically from inside a `.filter()`
 * predicate inside a `useMemo` -- idempotent (`Set.add`), so safe if React re-invokes
 * the same render (StrictMode's double-render, an aborted concurrent render), but
 * still a side effect during render rather than in an effect. Left this way
 * deliberately: moving it into a `useEffect` would hide the moved vehicle's tile one
 * render later than the vehicle's own moving state already does, which is worse for
 * this specific feature (the whole point is hiding it the instant it's known), not
 * safer.
 */
export function isSustainedAlertStillOpen(
  input: SustainedAlertCheck,
  deadSet: SustainedAlertDeadSet,
): boolean {
  const { alertId, ts, now, isLive, isMoving, isExcludedState = false } = input
  if (deadSet.isDead(alertId)) return false
  if (!isWithinSustainedAlertMaxAge(ts, now)) return false
  if (!isLive) return false
  if (isMoving || isExcludedState) {
    deadSet.markDead(alertId)
    return false
  }
  return true
}
